// AdminRouteTracking.jsx — FIXED VERSION WITH AUTH TOKEN & BLACK TEXT

import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API_BASE_URL = "https://patrol-react.eblackdog.net";

function getAuthToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken") ||
    localStorage.getItem("jwt") ||
    ""
  );
}

const ICON = {
  pinSizeFrom: 44,
  pinSizeTo: 44,
  userSize: 48,
  cpRadius: 9,
};

const GAP_MINUTES = 5;
const GAP_MS = GAP_MINUTES * 60 * 1000;
const MAX_JUMP_METERS = 300;
const MAX_SPEED_MPS = 25;
const MAX_ACCURACY_METERS = 50;

const POLL_FAST_MS = 3000;
const POLL_SLOW_MS = 8000;

const COMPLETED_STATUSES = new Set(["EARLY", "ONTIME", "DELAY", "COMPLETED"]);

function perpendicularDist(pt, lineStart, lineEnd) {
  const [x, y] = pt;
  const [x1, y1] = lineStart;
  const [x2, y2] = lineEnd;
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (dx === 0 && dy === 0) return Math.hypot(x - x1, y - y1);
  const t = ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy);
  return Math.hypot(x - (x1 + t * dx), y - (y1 + t * dy));
}

function rdpSimplify(points, epsilon) {
  if (points.length < 3) return points;

  let maxDist = 0;
  let maxIdx = 0;

  for (let i = 1; i < points.length - 1; i++) {
    const d = perpendicularDist(points[i], points[0], points[points.length - 1]);
    if (d > maxDist) {
      maxDist = d;
      maxIdx = i;
    }
  }

  if (maxDist > epsilon) {
    const left = rdpSimplify(points.slice(0, maxIdx + 1), epsilon);
    const right = rdpSimplify(points.slice(maxIdx), epsilon);
    return [...left.slice(0, -1), ...right];
  }

  return [points[0], points[points.length - 1]];
}

function catmullRomSpline(points, numSegments = 6) {
  if (points.length < 2) return points;

  const pts = points.length > 300 ? rdpSimplify(points, 0.00003) : points;
  const result = [];

  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(i - 1, 0)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(i + 2, pts.length - 1)];

    for (let t = 0; t < numSegments; t++) {
      const tt = t / numSegments;
      const tt2 = tt * tt;
      const tt3 = tt2 * tt;

      const lat =
        0.5 *
        (2 * p1[0] +
          (-p0[0] + p2[0]) * tt +
          (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * tt2 +
          (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * tt3);

      const lng =
        0.5 *
        (2 * p1[1] +
          (-p0[1] + p2[1]) * tt +
          (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * tt2 +
          (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * tt3);

      result.push([lat, lng]);
    }
  }

  result.push(pts[pts.length - 1]);
  return result;
}

function animateMarker(marker, targetLatLng, durationMs = 900) {
  return new Promise((resolve) => {
    if (!marker) return resolve();

    const start = marker.getLatLng();
    const dLat = targetLatLng[0] - start.lat;
    const dLng = targetLatLng[1] - start.lng;

    if (Math.abs(dLat) < 0.0000001 && Math.abs(dLng) < 0.0000001) {
      marker.setLatLng(targetLatLng);
      return resolve();
    }

    const startTime = performance.now();

    function step(now) {
      const elapsed = now - startTime;
      const raw = Math.min(elapsed / durationMs, 1);
      const t =
        raw < 0.5
          ? 4 * raw * raw * raw
          : 1 - Math.pow(-2 * raw + 2, 3) / 2;

      marker.setLatLng([start.lat + dLat * t, start.lng + dLng * t]);

      if (raw < 1) requestAnimationFrame(step);
      else resolve();
    }

    requestAnimationFrame(step);
  });
}

function pointKey(pt, ts = "") {
  return `${Number(pt[0]).toFixed(7)},${Number(pt[1]).toFixed(7)},${ts || ""}`;
}

function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (v) => (v * Math.PI) / 180;

  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return 2 * R * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

function parseDbIst(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return Number.isNaN(ts.getTime()) ? null : ts;
  if (typeof ts !== "string") return null;

  const s = ts.trim();
  if (!s) return null;

  const value = /Z$|[+-]\d{2}:?\d{2}$/.test(s)
    ? s
    : `${s.replace(" ", "T")}Z`;

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function durationForMove(fromPt, toPt, fromTs, toTs) {
  const dist = haversineMeters(
    { lat: fromPt[0], lng: fromPt[1] },
    { lat: toPt[0], lng: toPt[1] }
  );

  const fromMs = parseDbIst(fromTs)?.getTime() ?? null;
  const toMs = parseDbIst(toTs)?.getTime() ?? null;

  const realGapMs =
    fromMs != null && toMs != null ? Math.max(500, toMs - fromMs) : 1000;

  const byDistance = Math.min(2200, Math.max(650, dist * 90));
  const byTime = Math.min(1800, Math.max(650, realGapMs * 0.35));

  return Math.round(Math.min(byDistance, byTime));
}

function pinSVG(fill = "#111827", stroke = "#111827", size = 40) {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="${stroke}" stroke-width="1.5" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22s7-6.33 7-12a7 7 0 1 0-14 0c0 5.67 7 12 7 12z" fill="${fill}" opacity="0.14"/>
    <circle cx="12" cy="10" r="3.25" fill="${fill}"/>
    <path d="M12 22s7-6.33 7-12a7 7 0 1 0-14 0c0 5.67 7 12 7 12z"/>
  </svg>`;
}

function userSVG(color = "#2563EB", size = 44) {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="${color}" stroke-width="1.5" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="8.5" r="3.5" fill="${color}" opacity="0.12"/>
    <path d="M4 19a8 8 0 0 1 16 0" fill="${color}" opacity="0.08"/>
    <circle cx="12" cy="8.5" r="3.5"/>
    <path d="M4 19a8 8 0 0 1 16 0"/>
  </svg>`;
}

function pinIcon(fill, stroke, size) {
  return L.divIcon({
    className: "pin",
    html: pinSVG(fill, stroke, size),
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
  });
}

function userIcon(color, size) {
  return L.divIcon({
    className: "userIcon",
    html: `<div class="pulse"></div>${userSVG(color, size)}`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function badgeIcon(text, bg, textColor = "#ffffff") {
  return L.divIcon({
    className: "custom-badge",
    html: `<div style="transform:translate(-50%,-50%); background:${bg}; color:${textColor}; font-size:10px; font-weight:700; padding:3px 8px; border-radius:999px; border:2px solid #fff; box-shadow:0 2px 6px rgba(0,0,0,0.3); white-space:nowrap; text-align:center; min-width:50px;">${text}</div>`,
    iconSize: [60, 24],
    iconAnchor: [30, 12],
  });
}

function loginIcon() {
  return badgeIcon("LOGIN", "#16a34a", "#ffffff");
}

function logoutIcon() {
  return badgeIcon("LOGOUT", "#8b5cf6", "#ffffff");
}

function formatISTFromUtc(ts) {
  if (!ts) return "—";

  const d =
    ts instanceof Date
      ? ts
      : new Date(/Z$|[+-]\d{2}:?\d{2}$/.test(ts) ? ts : `${ts}Z`);

  if (Number.isNaN(d.getTime())) return ts || "—";

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  })
    .format(d)
    .replace(",", "");
}

function isValidLatLng(p) {
  return (
    p &&
    Number.isFinite(Number(p.lat)) &&
    Number.isFinite(Number(p.lng))
  );
}

function cleanTrack(rawTrack) {
  let points = rawTrack
    .filter((p) => isValidLatLng(p))
    .map((p) => ({
      ...p,
      lat: Number(p.lat),
      lng: Number(p.lng),
      accuracy: p.accuracy == null ? null : Number(p.accuracy),
    }))
    .filter((p) => p.accuracy == null || p.accuracy <= MAX_ACCURACY_METERS);

  const seen = new Set();

  points = points.filter((p) => {
    const key = `${p.lat.toFixed(6)},${p.lng.toFixed(6)},${p.ts}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return points.sort(
    (a, b) =>
      (parseDbIst(a.ts)?.getTime() ?? 0) -
      (parseDbIst(b.ts)?.getTime() ?? 0)
  );
}

export default function AdminRouteTracking() {
  const location = useLocation();

  const mapRef = useRef(null);
  const latestMarkerRef = useRef(null);
  const userPathLayerRef = useRef(null);
  const loginLogoutLayerRef = useRef(null);
  const staticLayersRef = useRef(false);
  const didInitialFitRef = useRef(false);
  const checkpointsLayerRef = useRef(null);
  const geofenceLayerRef = useRef(null);
  const routeLineLayerRef = useRef(null);
  const routeEndPointsRef = useRef(null);

  const pollTimeoutRef = useRef(null);
  const lastPositionKeyRef = useRef("");
  const isFetchingRef = useRef(false);

  const animatedPathLayerRef = useRef(null);
  const animationQueueRef = useRef([]);
  const isAnimatingRef = useRef(false);
  const lastAnimatedKeyRef = useRef("");
  const lastBadgeSigRef = useRef("");
  const errorShownRef = useRef(false);

  const dashboardData = useMemo(() => {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    return {
      company_name:
        localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor:
        localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(
        localStorage.getItem("admin_active") ?? userData.admin_active ?? 0
      ),
      company_address:
        localStorage.getItem("company_address") ||
        userData.company_address ||
        "",
      company_phone:
        localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email:
        localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo:
        localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };
  }, []);

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  const queryData = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return {
      routeId: Number(params.get("route_id") || 0),
      userId: params.get("user_id") || "",
      allocId: Number(params.get("alloc_id") || 0),
    };
  }, [location.search]);

  const apiUrl = useMemo(() => {
    const params = new URLSearchParams({
      route_id: String(queryData.routeId),
      limit: "5000",
    });

    if (queryData.userId) params.set("user_id", queryData.userId);
    if (queryData.allocId) params.set("alloc_id", String(queryData.allocId));

    return `${API_BASE_URL}/api/routeTracking?${params.toString()}`;
  }, [queryData]);

  const setStatus = useCallback((id, text, type = "neutral") => {
    const el = document.getElementById(id);
    if (!el) return;

    el.textContent = text;

    const base = "px-3 py-1 text-xs font-semibold rounded-full";
    const classes = {
      ok: "bg-green-100 text-green-700",
      warn: "bg-red-100 text-red-700",
      neutral: "bg-slate-200 text-slate-800",
    };

    el.className = `${base} ${classes[type] || classes.neutral}`;
  }, []);

  const statusSet = useCallback(
    (t, c = "neutral") => setStatus("fenceStatus", t, c),
    [setStatus]
  );

  const routeStatusSet = useCallback(
    (t, c = "neutral") => setStatus("routeStatus", t, c),
    [setStatus]
  );

  const drawStaticLayers = useCallback((data) => {
    const map = mapRef.current;
    if (!map || staticLayersRef.current) return;

    const bounds = L.latLngBounds();
    const from = data.route?.meta?.from || null;
    const to = data.route?.meta?.to || null;
    const endMarkers = [];

    if (isValidLatLng(from)) {
      const lat = Number(from.lat);
      const lng = Number(from.lng);

      endMarkers.push(
        L.marker([lat, lng], {
          icon: pinIcon("#16a34a", "#166534", ICON.pinSizeFrom),
        }).bindTooltip("From", { 
          direction: "top",
          className: "custom-tooltip"
        })
      );

      bounds.extend([lat, lng]);
    }

    if (isValidLatLng(to)) {
      const lat = Number(to.lat);
      const lng = Number(to.lng);

      endMarkers.push(
        L.marker([lat, lng], {
          icon: pinIcon("#dc2626", "#991b1b", ICON.pinSizeTo),
        }).bindTooltip("To", { 
          direction: "top",
          className: "custom-tooltip"
        })
      );

      bounds.extend([lat, lng]);
    }

    if (endMarkers.length) {
      routeEndPointsRef.current = L.layerGroup(endMarkers).addTo(map);
    }

    const routeLine = data.route?.line?.geometry?.coordinates || [];

    if (Array.isArray(routeLine) && routeLine.length >= 2) {
      const coordsLL = routeLine
        .map(([lng, lat]) => [Number(lat), Number(lng)])
        .filter(([la, ln]) => Number.isFinite(la) && Number.isFinite(ln));

      if (coordsLL.length >= 2) {
        routeLineLayerRef.current = L.polyline(coordsLL, {
          color: "#0ea5e9",
          weight: 4,
          opacity: 0.75,
          dashArray: "5,7",
        }).addTo(map);

        coordsLL.forEach((c) => bounds.extend(c));
      }
    }

    const cps = Array.isArray(data.checkpoints) ? data.checkpoints : [];

    const cpMarkers = cps
      .filter((cp) => isValidLatLng(cp))
      .map((cp) => {
        const lat = Number(cp.lat);
        const lng = Number(cp.lng);

        bounds.extend([lat, lng]);

        return L.circleMarker([lat, lng], {
          radius: ICON.cpRadius,
          color: "#16a34a",
          weight: 2,
          fillColor: "#22c55e",
          fillOpacity: 0.35,
        }).bindTooltip(cp.checkpoint_name || "Checkpoint", {
          direction: "top",
          className: "custom-tooltip"
        });
      });

    if (cpMarkers.length) {
      checkpointsLayerRef.current = L.layerGroup(cpMarkers).addTo(map);
    }

    const gf = data.geofences?.features || [];
    const gfShapes = [];

    gf.forEach((f) => {
      const p = f.properties || {};

      if (p.type === "CIRCLE" && f.geometry?.type === "Point") {
        const [lngR, latR] = f.geometry.coordinates || [];
        const lat = Number(latR);
        const lng = Number(lngR);

        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          gfShapes.push(
            L.circle([lat, lng], {
              radius: Number(p.radius_m || 0),
              color: "#f59e0b",
              weight: 2,
              fillOpacity: 0.08,
            }).bindPopup(`<div class="custom-popup"><b>${p.name || "Fence"}</b><br>Circle ${p.radius_m || 0}m</div>`)
          );

          bounds.extend([lat, lng]);
        }
      }

      if (p.type === "POLYGON" && f.geometry?.type === "Polygon") {
        const ring = (f.geometry.coordinates?.[0] || [])
          .map(([ln, la]) => [Number(la), Number(ln)])
          .filter(([la, ln]) => Number.isFinite(la) && Number.isFinite(ln));

        if (ring.length) {
          gfShapes.push(
            L.polygon(ring, {
              color: "#ef4444",
              weight: 2,
              fillOpacity: 0.06,
            }).bindPopup(`<div class="custom-popup"><b>${p.name || "Fence"}</b><br>Polygon</div>`)
          );

          ring.forEach((c) => bounds.extend(c));
        }
      }
    });

    if (gfShapes.length) {
      geofenceLayerRef.current = L.layerGroup(gfShapes).addTo(map);
    }

    staticLayersRef.current = true;

    if (bounds.isValid() && !didInitialFitRef.current) {
      map.fitBounds(bounds.pad(0.15));
      didInitialFitRef.current = true;
    }
  }, []);

  const runAnimationQueue = useCallback(async () => {
    if (isAnimatingRef.current) return;

    isAnimatingRef.current = true;

    try {
      while (animationQueueRef.current.length) {
        const item = animationQueueRef.current.shift();

        if (latestMarkerRef.current) {
          await animateMarker(latestMarkerRef.current, item.pt, item.durationMs);
          latestMarkerRef.current.setTooltipContent(item.tooltip || "");

          if (
            animatedPathLayerRef.current &&
            typeof animatedPathLayerRef.current.addLatLng === "function"
          ) {
            animatedPathLayerRef.current.addLatLng(item.pt);
          }
        }

        lastAnimatedKeyRef.current = item.key;
      }
    } finally {
      isAnimatingRef.current = false;
    }
  }, []);

  const drawLiveLayers = useCallback(
    (data) => {
      const map = mapRef.current;
      if (!map) return false;

      const guards = data.gps?.guards || [];
      const completion = data.completion || null;

      const isCompleted = COMPLETED_STATUSES.has(
        String(completion?.status || "").toUpperCase()
      );

      let posChanged = false;
      let latestTsRaw = null;

      for (const g of guards) {
        const rawTrack = Array.isArray(g.track) ? g.track : [];
        const trackingStartMs =
          parseDbIst(data?.tracking?.start_time)?.getTime() ?? null;

        const orderedTrack = cleanTrack(rawTrack).filter((p) => {
          if (!trackingStartMs) return true;
          return (parseDbIst(p.ts)?.getTime() ?? 0) >= trackingStartMs - 30000;
        });

        if (!orderedTrack.length) continue;

        const allPoints = orderedTrack.map((p) => [p.lat, p.lng]);
        const firstPt = allPoints[0];
        const lastPt = allPoints[allPoints.length - 1];
        const firstTs = orderedTrack[0].ts;
        const lastTs = orderedTrack[orderedTrack.length - 1].ts;

        const loginPt = [
          Number(data?.tracking?.start_latitude ?? firstPt[0]),
          Number(data?.tracking?.start_longitude ?? firstPt[1]),
        ];

        const badgeSig = `${firstTs}-${
          isCompleted ? completion?.completed_at || lastTs : "ACTIVE"
        }`;

        if (badgeSig !== lastBadgeSigRef.current || !loginLogoutLayerRef.current) {
          if (loginLogoutLayerRef.current) {
            map.removeLayer(loginLogoutLayerRef.current);
          }

          const markers = [
            L.marker(loginPt, { icon: loginIcon() }).bindTooltip(
              `Login<br>${formatISTFromUtc(data?.tracking?.start_time || firstTs)}`,
              { direction: "top", className: "custom-tooltip" }
            ),
          ];

          if (isCompleted) {
            markers.push(
              L.marker(lastPt, { icon: logoutIcon() }).bindTooltip(
                `Logout<br>${formatISTFromUtc(completion?.completed_at || lastTs)}`,
                { direction: "top", className: "custom-tooltip" }
              )
            );
          }

          loginLogoutLayerRef.current = L.layerGroup(markers).addTo(map);
          lastBadgeSigRef.current = badgeSig;
        }

        if (isCompleted) {
          if (userPathLayerRef.current) map.removeLayer(userPathLayerRef.current);
          if (animatedPathLayerRef.current) map.removeLayer(animatedPathLayerRef.current);

          userPathLayerRef.current = L.polyline(allPoints, {
            weight: 6,
            opacity: 0.92,
            color: "#0ea5e9",
            lineJoin: "round",
            lineCap: "round",
          }).addTo(map);

          if (latestMarkerRef.current) {
            map.removeLayer(latestMarkerRef.current);
            latestMarkerRef.current = null;
          }
        } else {
          const tooltip = `Name: ${
            g.guard_name || g.guard_id || "Guard"
          }<br>Time: ${formatISTFromUtc(lastTs)}`;

          if (!latestMarkerRef.current) {
            latestMarkerRef.current = L.marker(loginPt, {
              icon: userIcon("#2563EB", ICON.userSize),
              zIndexOffset: 1200,
            })
              .bindTooltip(tooltip, { direction: "top", className: "custom-tooltip" })
              .addTo(map);

            animatedPathLayerRef.current = L.polyline([loginPt], {
              weight: 5,
              opacity: 0.95,
              color: "#0ea5e9",
              smoothFactor: 1,
            }).addTo(map);
          } else {
            latestMarkerRef.current.setTooltipContent(tooltip);
          }

          const pointItems = orderedTrack.map((p, idx) => ({
            pt: [p.lat, p.lng],
            key: pointKey([p.lat, p.lng], p.ts),
            prev:
              idx > 0
                ? [orderedTrack[idx - 1].lat, orderedTrack[idx - 1].lng]
                : [p.lat, p.lng],
            prevTs: idx > 0 ? orderedTrack[idx - 1].ts : p.ts,
            ts: p.ts,
            tooltip,
          }));

          const lastIdx = pointItems.findIndex(
            (x) => x.key === lastAnimatedKeyRef.current
          );

          const newItems = lastIdx >= 0 ? pointItems.slice(lastIdx + 1) : pointItems;

          newItems.forEach((item) => {
            if (!animationQueueRef.current.some((q) => q.key === item.key)) {
              animationQueueRef.current.push({
                ...item,
                durationMs: durationForMove(item.prev, item.pt, item.prevTs, item.ts),
              });
            }
          });

          runAnimationQueue();
        }

        latestTsRaw = lastTs;

        if (pointKey(lastPt, lastTs) !== lastPositionKeyRef.current) {
          posChanged = true;
          lastPositionKeyRef.current = pointKey(lastPt, lastTs);
        }
      }

      const lastTsEl = document.getElementById("lastTs");
      if (lastTsEl) {
        lastTsEl.textContent = latestTsRaw
          ? `Last update: ${formatISTFromUtc(latestTsRaw)}`
          : "Last update: —";
      }

      routeStatusSet(
        isCompleted ? "Route status: Completed" : "Route status: In progress",
        isCompleted ? "ok" : "neutral"
      );

      return posChanged;
    },
    [runAnimationQueue, routeStatusSet]
  );

  const refresh = useCallback(async () => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;

    try {
      const token = getAuthToken();

      if (!token) {
        throw new Error("Login token missing. Please login again.");
      }

      const response = await fetch(apiUrl, {
        method: "GET",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        throw new Error(data?.message || "Unauthorized. Please login again.");
      }

      if (!response.ok || !(data.ok || data.success)) {
        throw new Error(data?.error || data?.message || `HTTP ${response.status}`);
      }

      errorShownRef.current = false;

      drawStaticLayers(data);
      drawLiveLayers(data);
    } catch (error) {
      console.error("[routeTracking] fetch failed:", error);

      statusSet(error.message || "API error", "warn");

      if (!errorShownRef.current) {
        toast.error(error.message || "Failed to fetch tracking data");
        errorShownRef.current = true;
      }
    } finally {
      isFetchingRef.current = false;

      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }

      pollTimeoutRef.current = setTimeout(refresh, POLL_FAST_MS);
    }
  }, [apiUrl, drawStaticLayers, drawLiveLayers, statusSet]);

  useEffect(() => {
    if (!queryData.routeId) {
      toast.error("Route ID is required for tracking");
      return;
    }

    latestMarkerRef.current = null;
    userPathLayerRef.current = null;
    loginLogoutLayerRef.current = null;
    staticLayersRef.current = false;
    didInitialFitRef.current = false;
    lastPositionKeyRef.current = "";
    animationQueueRef.current = [];
    isAnimatingRef.current = false;
    lastAnimatedKeyRef.current = "";
    lastBadgeSigRef.current = "";
    errorShownRef.current = false;

    const map = L.map("map", { zoomControl: true }).setView([20.5937, 78.9629], 5);
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 20,
      attribution: "&copy; OpenStreetMap",
    }).addTo(map);

    L.control.scale({ imperial: false }).addTo(map);

    const legend = L.control({ position: "bottomright" });

    legend.onAdd = () => {
      const d = L.DomUtil.create("div", "legend");

      d.innerHTML = `
        <div><span class="chip" style="background:#0ea5e9"></span>Track</div>
        <div><span class="chip" style="background:#ef4444"></span>Fence (Polygon)</div>
        <div><span class="chip" style="background:#f59e0b"></span>Fence (Circle)</div>
        <div><span class="chip" style="background:#22c55e"></span>Checkpoints</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#16a34a;border-radius:3px;"></span> From</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#dc2626;border-radius:3px;"></span> To</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#2563EB;border-radius:50%;"></span> Live user</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#16a34a;border-radius:999px;"></span> Login</div>
        <div><span style="display:inline-block;width:14px;height:14px;background:#8b5cf6;border-radius:999px;"></span> Logout</div>`;

      return d;
    };

    legend.addTo(map);

    refresh();

    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }

      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, [queryData.routeId, refresh]);

  if (!queryData.routeId) {
    return (
      <Layout
        dashboardData={dashboardData}
        routePage={routePage}
        activePage="tracking"
        activeMenu="tracking"
      >
        <Toaster position="top-right" />

        <div className="p-5 text-[var(--text-primary)] bg-[var(--card-bg)] rounded-2xl border border-[var(--card-border)] shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <h2 className="text-[18px] font-extrabold text-[var(--text-primary)]">
            Route ID Required
          </h2>
          <p className="text-[13px] text-[var(--text-muted)]">
            Example: <b>?route_id=123</b>
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="tracking"
      activeMenu="tracking"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
        }}
      />

      <style>{`
        #map {
          height: 100%;
          width: 100%;
          min-height: 520px;
        }

        .legend {
          background: #fff;
          padding: 10px 12px;
          border-radius: 10px;
          box-shadow: 0 2px 12px rgba(0,0,0,.12);
          font: 12px/1.25 system-ui;
          color: #1a1a1a;
        }

        .legend div {
          margin: 6px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .chip {
          display: inline-block;
          width: 14px;
          height: 14px;
          border-radius: 3px;
        }

        .pin {
          transform: translate(-50%, -100%);
        }

        .userIcon {
          transform: translate(-50%, -50%);
        }

        .custom-badge {
          background: transparent !important;
          box-shadow: none !important;
        }

        /* Custom tooltip with black text */
        .custom-tooltip {
          background: #ffffff !important;
          color: #1a1a1a !important;
          border: 1px solid #d1d5db !important;
          border-radius: 8px !important;
          padding: 6px 12px !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
          font-family: 'Inter', system-ui, sans-serif !important;
        }

        .custom-tooltip::before {
          border-top-color: #ffffff !important;
        }

        .custom-tooltip strong {
          color: #1a1a1a !important;
        }

        /* Custom popup with black text */
        .custom-popup {
          color: #1a1a1a !important;
          font-family: 'Inter', system-ui, sans-serif !important;
        }

        .custom-popup b {
          color: #1a1a1a !important;
        }

        .leaflet-popup-content {
          color: #1a1a1a !important;
          font-family: 'Inter', system-ui, sans-serif !important;
        }

        .leaflet-popup-content-wrapper {
          border-radius: 8px !important;
          box-shadow: 0 2px 12px rgba(0,0,0,0.15) !important;
        }

        .leaflet-popup-tip {
          background: #ffffff !important;
        }

        .pulse {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 18px;
          height: 18px;
          border-radius: 999px;
          transform: translate(-50%, -50%);
          animation: pulse 1.6s infinite;
        }

        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(37,99,235,.55);
          }

          70% {
            box-shadow: 0 0 0 18px rgba(37,99,235,0);
          }

          100% {
            box-shadow: 0 0 0 0 rgba(37,99,235,0);
          }
        }

        .tracking-header {
          background: var(--card-bg);
          border-bottom: 1px solid var(--divider);
          color: var(--text-primary);
        }

        .tracking-container {
          background: var(--page-bg);
          border-color: var(--card-border);
        }
      `}</style>

      <div className="tracking-container h-[calc(100vh-90px)] min-h-[650px] bg-[var(--page-bg)] rounded-2xl overflow-hidden border border-[var(--card-border)] shadow-[0_8px_32px_rgba(0,0,0,0.3)] flex flex-col">
        <header className="tracking-header bg-[var(--card-bg)] border-b border-[var(--divider)] px-4 py-3 flex flex-wrap items-center gap-3">
          <h3 className="m-0 text-lg font-extrabold text-[var(--text-primary)]">
            Route #{queryData.routeId}
            {queryData.allocId ? ` — Allocation #${queryData.allocId}` : ""}
          </h3>

          <div id="guardInfo" className="text-sm text-[var(--text-secondary)]">
            Guard: —
          </div>

          <div id="sessionRange" className="text-xs text-[var(--text-muted)]">
            Session: —
          </div>

          <div
            id="routeStatus"
            className="px-3 py-1 text-xs font-extrabold rounded-full bg-[var(--surface)] text-[var(--text-primary)]"
          >
            Route status: —
          </div>

          <div
            id="fenceStatus"
            className="px-3 py-1 text-xs font-extrabold rounded-full bg-[var(--surface)] text-[var(--text-primary)]"
          >
            No fence
          </div>

          <div id="lastTs" className="ml-auto text-xs text-[var(--text-faint)]">
            —
          </div>

          <div
            id="pollIndicator"
            className="px-2 py-1 text-xs font-extrabold rounded-full bg-[var(--accent)] text-white"
          >
            ↻ 3.0s
          </div>
        </header>

        <div className="flex-1">
          <div id="map" />
        </div>
      </div>
    </Layout>
  );
}